import { useMemo, useState } from "react";
import { AlertTriangle, Check, Link2, Link2Off, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { rotuloTipo } from "@/lib/tipoProfissional";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";

// ============================================================
// F0 — Vínculos com a clínica (só admin)
// ============================================================
// Sem este de-para, nenhuma tela de dinheiro pode existir: o escalas conhece
// "Dra Taissa", o ERP conhece "Taíssa Aquino", e nada liga os dois. A máquina
// PROPÕE (e-mail vale como quase-prova, nome é palpite); quem confirma é o
// Bryan, linha a linha. Nada é gravado sozinho.

const SEM_VINCULO = "0";

export function AdminVinculos() {
  const [incluirInativos, setIncluirInativos] = useState(false);
  const q = trpc.vinculos.sugerir.useQuery({ incluirInativos });
  const utils = trpc.useUtils();
  const [escolhas, setEscolhas] = useState<Record<number, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [salvoAgora, setSalvoAgora] = useState<Record<number, boolean>>({});

  const salvar = trpc.vinculos.salvar.useMutation({
    onSuccess: (_r, vars) => {
      setErro(null);
      setSalvoAgora((s) => ({ ...s, [vars.profissionalId]: true }));
      utils.vinculos.sugerir.invalidate();
      utils.vinculos.listar.invalidate();
    },
    onError: (e) => setErro(e.message),
  });

  const clinica = q.data?.clinica ?? [];
  const itens = q.data?.itens ?? [];

  const naoVinculados = useMemo(() => itens.filter((i) => !i.vinculo).length, [itens]);

  function valorAtual(item: (typeof itens)[number]): string {
    const escolhido = escolhas[item.id];
    if (escolhido !== undefined) return escolhido;
    if (item.vinculo) return String(item.vinculo.vcmProfissionalId);
    if (item.sugestao.vcmProfissionalId) return String(item.sugestao.vcmProfissionalId);
    return SEM_VINCULO;
  }

  function confirmar(item: (typeof itens)[number]) {
    const valor = valorAtual(item);
    salvar.mutate({
      profissionalId: item.id,
      vcmProfissionalId: valor === SEM_VINCULO ? null : Number(valor),
    });
  }

  if (q.isLoading) {
    return <div className="py-10 text-center text-slate-500">Carregando vínculos…</div>;
  }

  if (q.error) {
    return (
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-red-700">Não deu para falar com a clínica</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-700">
          <p>{q.error.message}</p>
          <p className="text-slate-500">
            O de-para lê o cadastro da clínica pela rede interna. Se o canal estiver fora do ar, a
            tela não inventa nome nenhum — ela simplesmente não abre.
          </p>
          <Button variant="outline" onClick={() => q.refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" /> Tentar de novo
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vínculos com a clínica</h1>
          <p className="text-sm text-slate-600">
            Cada profissional da escala precisa apontar para a ficha dele no sistema da clínica.
            Enquanto isso não estiver feito, nenhuma apuração de dinheiro sai daqui.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={incluirInativos}
              onChange={(e) => setIncluirInativos(e.target.checked)}
            />
            Mostrar inativos
          </label>
          <Button variant="outline" size="sm" onClick={() => q.refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" /> Atualizar
          </Button>
        </div>
      </div>

      {naoVinculados > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>{naoVinculados}</strong>{" "}
            {naoVinculados === 1 ? "profissional ainda não está vinculado" : "profissionais ainda não estão vinculados"}{" "}
            à clínica. Confira a sugestão de cada linha antes de confirmar — casar por nome erra
            mais do que acerta.
          </span>
        </div>
      )}

      {erro && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {erro}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="relative w-full overflow-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Profissional (escala)</th>
                  <th className="px-4 py-3">Login</th>
                  <th className="px-4 py-3">Sugestão</th>
                  <th className="px-4 py-3">Ficha na clínica</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item) => {
                  const vinculado = Boolean(item.vinculo);
                  const valor = valorAtual(item);
                  const mudou = valor !== (item.vinculo ? String(item.vinculo.vcmProfissionalId) : SEM_VINCULO);
                  return (
                    <tr
                      key={item.id}
                      className={
                        vinculado
                          ? "border-b border-slate-100"
                          : "border-b border-slate-100 bg-red-50/60"
                      }
                    >
                      <td className="px-4 py-3 align-top">
                        <div className={vinculado ? "font-medium text-slate-900" : "font-medium text-red-700"}>
                          {item.nome}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          <Badge variant="outline">{rotuloTipo(item.tipo)}</Badge>
                          {!item.ativo && <Badge variant="inativo">inativo na escala</Badge>}
                          {!vinculado && (
                            <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                              não vinculado
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3 align-top text-slate-600">
                        {item.semLogin ? (
                          <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                            sem login
                          </span>
                        ) : (
                          <>
                            <div className="break-all">{item.userEmail}</div>
                            {item.emailDuplicado && (
                              <div className="mt-1 text-xs text-amber-700">
                                e-mail repetido em mais de um login
                              </div>
                            )}
                          </>
                        )}
                      </td>

                      <td className="px-4 py-3 align-top">
                        {item.sugestao.vcmProfissionalId ? (
                          <>
                            <div className="text-slate-800">{item.sugestao.nome}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-1">
                              <Badge
                                variant={item.sugestao.confianca === "alta" ? "ativo" : "outline"}
                              >
                                confiança {item.sugestao.confianca}
                              </Badge>
                              {item.sugestao.ativoNaClinica === false && (
                                <Badge variant="inativo">ficha inativa</Badge>
                              )}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">{item.sugestao.motivo}</div>
                          </>
                        ) : (
                          <div className="text-xs text-slate-500">{item.sugestao.motivo}</div>
                        )}
                      </td>

                      <td className="px-4 py-3 align-top">
                        <Select
                          value={valor}
                          onValueChange={(v) => setEscolhas((s) => ({ ...s, [item.id]: v }))}
                        >
                          <SelectTrigger className="w-64">
                            <SelectValue placeholder="Escolher ficha" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SEM_VINCULO}>— sem vínculo —</SelectItem>
                            {clinica.map((c) => (
                              <SelectItem key={c.id} value={String(c.id)}>
                                {c.nome}
                                {c.ativo ? "" : " (inativo)"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {item.vinculo && !item.vinculo.existeNaClinica && (
                          <div className="mt-1 text-xs text-red-700">
                            a ficha #{item.vinculo.vcmProfissionalId} não existe mais na clínica
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-3 align-top text-right">
                        <Button
                          size="sm"
                          variant={mudou ? "default" : "outline"}
                          disabled={salvar.isPending}
                          onClick={() => confirmar(item)}
                        >
                          {valor === SEM_VINCULO ? (
                            <Link2Off className="mr-2 h-4 w-4" />
                          ) : (
                            <Link2 className="mr-2 h-4 w-4" />
                          )}
                          Confirmar
                        </Button>
                        {salvoAgora[item.id] && !mudou && (
                          <div className="mt-1 flex items-center justify-end gap-1 text-xs text-emerald-700">
                            <Check className="h-3 w-3" /> salvo
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {(q.data?.avisos.loginsDuplicados.length ?? 0) > 0 && (
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="text-base text-amber-800">Logins repetidos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-slate-700">
            <p className="text-slate-600">
              O mesmo e-mail aparece em mais de um login (diferem só na caixa das letras). O acesso
              já se vira: o SSO casa sem diferenciar maiúscula e desempata pelo último acesso. Mas
              para o dinheiro conviria manter um só — decisão do Bryan, não do sistema.
            </p>
            {q.data?.avisos.loginsDuplicados.map((d) => (
              <div key={d.email} className="rounded border border-amber-200 bg-amber-50 px-3 py-2">
                <div className="font-medium">{d.email}</div>
                <ul className="mt-1 list-inside list-disc text-xs text-slate-600">
                  {d.logins.map((l) => (
                    <li key={l.id}>
                      #{l.id} — {l.nome} — {l.email} — entrada por {l.loginMethod}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {(q.data?.avisos.semLogin.length ?? 0) > 0 && (
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="text-base text-amber-800">Sem login no escalas</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-700">
            <p className="mb-2 text-slate-600">
              Estes profissionais existem na escala mas não têm usuário. Podem ser vinculados à
              ficha da clínica normalmente (o vínculo é entre fichas, não entre logins), mas não
              recebem SSO nem aviso automático enquanto não tiverem acesso.
            </p>
            <ul className="list-inside list-disc">
              {q.data?.avisos.semLogin.map((s) => (
                <li key={s.id}>{s.nome}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
